import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Menu, LogOut, LayoutDashboard, Users, ArrowUp, Download, Moon, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import SocialLinks from "@/components/SocialLinks";
import WeatherBanner from "@/components/WeatherBanner";
import InstallPwa from "@/components/InstallPwa";
import PartnerBanner from "@/components/PartnerBanner";
import IdeaBox from "@/components/IdeaBox";
import MusicPlayer from "@/components/MusicPlayer";
import { useAuth } from "@/context/AuthContext";
import { getTheme, toggleTheme } from "@/utils/theme";
import { ASSO } from "@/constants";

let socialsTimer = null;

const NAV = [
  { label: "Accueil", path: "/", color: "#4f8f3f" },
  { label: "Qui sommes-nous", path: "/qui-sommes-nous", color: "#3f8f9e" },
  { label: "Événements", path: "/evenements", color: "#c2575f" },
  { label: "Actualités", path: "/actualites", color: "#b08d1e" },
  { label: "Vie des familles", path: "/vie-des-familles", color: "#c2575f" },
  { label: "Adresses utiles", path: "/adresses-utiles", color: "#8a6d4b" },
  { label: "Liste CETF", path: "/liste-cetf" },
  { label: "Boutique", path: "/boutique", color: "#c2575f" },
  { label: "Contact", path: "/contact", color: "#3f8f9e" },
];

const SEO_PAGES = {
  "/": ["Accueil", "Association Cœur d'École Toury Famille (CETF) à Toury : événements, calendrier scolaire, activités pour les enfants et les familles, adhésion et dons."],
  "/qui-sommes-nous": ["Qui sommes-nous", "Découvrez l'association CETF de Toury : missions, valeurs, engagements et l'équipe de bénévoles."],
  "/evenements": ["Événements", "Tous les événements de l'association CETF à Toury : fêtes, ateliers et manifestations familiales."],
  "/actualites": ["Actualités", "Les dernières actualités de l'association Cœur d'École Toury Famille (CETF)."],
  "/adresses-utiles": ["Adresses utiles", "Écoles de Toury, mairie, communauté de communes Cœur de Beauce et numéros utiles."],
  "/contact": ["Contact", "Contactez l'association Cœur d'École Toury Famille (CETF) à Toury."],
  "/connexion": ["Espace adhérent", "Connectez-vous à votre espace adhérent CETF."],
  "/connexion-bureau": ["Espace bureau", "Connexion à l'espace du bureau de l'association CETF."],
  "/boutique": ["CETF Store", "Boutique de l'association CETF : t-shirts, goodies et accessoires au profit des enfants de Toury."],
  "/liste-cetf": ["Liste CET", "Liste CET aux élections de parents d'élèves de l'école élémentaire Julien Mercier de Toury."],
};

function BackToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 500);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Revenir en haut de page"
      data-testid="back-to-top-btn"
      className={`fixed bottom-5 right-5 z-40 h-11 w-11 rounded-full bg-sky-700 text-white shadow-xl flex items-center justify-center transition-all duration-300 hover:bg-sky-800 hover:scale-110 ${
        show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6 pointer-events-none"
      }`}
    >
      <ArrowUp className="h-5 w-5" />
    </button>
  );
}

function Logo({ compact = false, dark = false }) {
  return (
    <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link" aria-label="Accueil Cœur d'École Toury Famille">
      <img src={ASSO.logoTreeUrl} alt="" className="h-11 w-auto object-contain logo-tree" />
      {!compact && (
        <span className="leading-tight">
          <span className={`block font-serif text-sm sm:text-base ${dark ? "text-white" : "text-slate-800"}`}>
            Cœur d'École – Toury Famille
          </span>
          <span className={`block font-serif text-xs ${dark ? "text-slate-400" : "text-slate-500"}`}>
            Association — CETF —
          </span>
        </span>
      )}
    </Link>
  );
}

export default function PublicLayout() {
  const { user, logout, isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [showSocials, setShowSocials] = useState(false);
  const [socialsOpen, setSocialsOpen] = useState(false);
  const [theme, setTheme] = useState(getTheme());
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onTheme = (e) => setTheme(e.detail);
    window.addEventListener("cetf:theme", onTheme);
    return () => window.removeEventListener("cetf:theme", onTheme);
  }, []);

  const [listLabel, setListLabel] = useState("Liste CET");
  const [listNavVisible, setListNavVisible] = useState(true);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/liste-cetf/public-info`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.list_title) setListLabel(d.list_title);
        if (d && d.nav_visible === false) setListNavVisible(false);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const [title, desc] = SEO_PAGES[location.pathname] || SEO_PAGES["/"];
    document.title = `${title} — CETF · Cœur d'École Toury Famille`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", desc);
  }, [location.pathname]);

  useEffect(() => {
    let vid = localStorage.getItem("cetf_vid");
    if (!vid) {
      vid = crypto.randomUUID();
      localStorage.setItem("cetf_vid", vid);
    }
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify({ path: location.pathname, vid }),
    }).catch(() => {});
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setShowSocials(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!showSocials) return undefined;
    setSocialsOpen(true);
    socialsTimer = setTimeout(() => setSocialsOpen(false), 4000);
    return () => clearTimeout(socialsTimer);
  }, [showSocials]);

  const expandSocials = () => {
    setSocialsOpen(true);
    clearTimeout(socialsTimer);
    socialsTimer = setTimeout(() => setSocialsOpen(false), 4000);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navItems = NAV
    .filter((item) => item.path !== "/liste-cetf" || listNavVisible)
    .map((item) => (item.path === "/liste-cetf" ? { ...item, label: listLabel } : item));

  const navLinks = (onClick) =>
    navItems.map((item) => (
      <NavLink
        key={item.path}
        to={item.path}
        onClick={onClick}
        data-testid={`nav-${item.path === "/" ? "accueil" : item.path.slice(1)}`}
        style={item.color ? { "--nav-c": item.color } : undefined}
        className={({ isActive }) =>
          item.path === "/liste-cetf"
            ? "text-sm font-semibold transition-colors text-emerald-700 hover:text-emerald-800 nav-pulse-cetf px-3 py-1"
            : `nav-leaf text-sm font-medium transition-colors ${isActive ? "active" : ""}`
        }
      >
        {item.label}
      </NavLink>
    ));

  return (
    <div className="min-h-screen flex flex-col">
      <WeatherBanner />
      <div
        className={`fixed top-24 right-0 z-40 transition-opacity duration-500 ${
          showSocials ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        data-testid="social-float"
      >
        <div
          className={`flex items-center transition-transform duration-500 ease-in-out ${socialsOpen ? "translate-x-0" : "translate-x-[calc(100%-2.6rem)]"}`}
          onMouseEnter={expandSocials}
        >
          <button
            type="button"
            onClick={() => (socialsOpen ? setSocialsOpen(false) : expandSocials())}
            aria-label={socialsOpen ? "Masquer les réseaux sociaux" : "Afficher les réseaux sociaux"}
            aria-expanded={socialsOpen}
            data-testid="social-float-tab"
            className="h-10 w-10 -mr-1 rounded-l-2xl bg-white/95 backdrop-blur border border-r-0 border-slate-200 shadow-xl flex items-center justify-center text-emerald-600 hover:text-emerald-700 shrink-0"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
              <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
              <path d="m8.6 13.5 6.8 4M15.4 6.5l-6.8 4" />
            </svg>
          </button>
          <div className="flex flex-col items-center bg-white/95 backdrop-blur rounded-full shadow-xl border border-slate-200 p-2">
            <SocialLinks vertical size="sm" />
          </div>
        </div>
      </div>
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Logo />
          <nav className="hidden lg:flex items-center gap-7" aria-label="Navigation principale">
            {navLinks()}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <button
              type="button"
              onClick={() => setTheme(toggleTheme())}
              aria-label={theme === "dark" ? "Passer au thème clair" : "Passer au thème sombre"}
              data-testid="theme-toggle-btn"
              className="h-9 w-9 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:text-sky-700 hover:border-slate-300 transition-colors"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            {user ? (
              <>
                {isAdmin && (
                  <Button variant="outline" size="sm" asChild data-testid="nav-admin-btn">
                    <Link to="/admin"><LayoutDashboard className="h-4 w-4 mr-2" />Back-office</Link>
                  </Button>
                )}
                <Button size="sm" className="bg-green-600 hover:bg-green-700" asChild data-testid="nav-espace-btn">
                  <Link to="/espace"><Users className="h-4 w-4 mr-2" />Espace adhérent</Link>
                </Button>
                <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="nav-logout-btn">
                  <LogOut className="h-4 w-4 mr-2" />Déconnexion
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" size="sm" asChild data-testid="nav-bureau-btn">
                  <Link to="/connexion-bureau">Espace bureau</Link>
                </Button>
                <Button size="sm" className="bg-green-600 hover:bg-green-700" asChild data-testid="nav-login-btn">
                  <Link to="/connexion">Espace adhérent</Link>
                </Button>
              </>
            )}
          </div>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn" aria-label="Ouvrir le menu">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 flex flex-col gap-6 pt-10">
              <Logo compact={false} />
              <nav className="flex flex-col gap-4" aria-label="Navigation mobile">{navLinks(() => setOpen(false))}</nav>
              <button
                type="button"
                onClick={() => setTheme(toggleTheme())}
                data-testid="theme-toggle-mobile-btn"
                className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-medium text-slate-700 hover:text-sky-700 transition-colors"
              >
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                {theme === "dark" ? "Thème clair" : "Thème sombre"}
              </button>
              <div className="mt-auto flex flex-col gap-2">
                {user ? (
                  <>
                    {isAdmin && (
                      <Button variant="outline" asChild data-testid="mobile-nav-admin-btn">
                        <Link to="/admin" onClick={() => setOpen(false)}>Back-office</Link>
                      </Button>
                    )}
                    <Button className="bg-green-600 hover:bg-green-700" asChild data-testid="mobile-nav-espace-btn">
                      <Link to="/espace" onClick={() => setOpen(false)}>Espace adhérent</Link>
                    </Button>
                    <Button variant="ghost" onClick={() => { setOpen(false); handleLogout(); }} data-testid="mobile-nav-logout-btn">
                      Déconnexion
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" asChild data-testid="mobile-nav-bureau-btn">
                      <Link to="/connexion-bureau" onClick={() => setOpen(false)}>Espace bureau</Link>
                    </Button>
                    <Button className="bg-green-600 hover:bg-green-700" asChild data-testid="mobile-nav-login-btn">
                      <Link to="/connexion" onClick={() => setOpen(false)}>Espace adhérent</Link>
                    </Button>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {!listNavVisible && <PartnerBanner />}

      <main className="flex-1">
        <Outlet />
      </main>
      <BackToTop />
      <InstallPwa />
      <MusicPlayer />

      <footer className="bg-slate-900 text-slate-300" data-testid="public-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-3 gap-10">
          <div className="space-y-4">
            <div className="flex items-center gap-2.5 group">
              <img src={ASSO.logoTreeUrl} alt="" className="h-10 w-auto object-contain logo-tree" />
              <span className="leading-tight">
                <span className="block font-serif text-sm text-white">Cœur d'École – Toury Famille</span>
                <span className="block font-serif text-xs text-slate-400">Association — CETF —</span>
              </span>
            </div>
            <p className="text-sm leading-relaxed text-slate-400">
              Activités socio-éducatives et socio-culturelles, manifestations conviviales et solidaires,
              défense des intérêts des enfants et des familles à Toury.
            </p>
          </div>
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">Navigation</h4>
            <ul className="space-y-2 text-sm">
              {navItems.map((item) => (
                <li key={item.path}>
                  <Link to={item.path} className="hover:text-white transition-colors" data-testid={`footer-nav-${item.path === "/" ? "accueil" : item.path.slice(1)}`}>
                    {item.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/mentions-legales" className="hover:text-white transition-colors" data-testid="footer-mentions-legales-link">Mentions légales</Link>
              </li>
              <li>
                <Link to="/politique-confidentialite" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Politique de confidentialité</Link>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">Contact & adhésion</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a href={`mailto:${ASSO.email}`} className="hover:text-white transition-colors" data-testid="footer-email-link">{ASSO.email}</a>
              </li>
              <li>
                <a href={ASSO.helloassoUrl} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" data-testid="footer-helloasso-link">
                  Adhérer via HelloAsso (10 €/an)
                </a>
              </li>
              <li>Président : {ASSO.president}</li>
              <li>
                <button
                  onClick={() => {
                    const p = window.__pwaPrompt;
                    if (p) {
                      p.prompt();
                      p.userChoice.catch(() => null).finally(() => { window.__pwaPrompt = null; });
                    } else {
                      window.dispatchEvent(new Event("cetf:install"));
                    }
                  }}
                  className="hover:text-white transition-colors inline-flex items-center gap-1.5"
                  data-testid="footer-install-btn"
                >
                  <Download className="h-3.5 w-3.5" /> Installer l'application
                </button>
              </li>
            </ul>
            <div className="mt-5">
              <h4 className="font-heading font-semibold text-white mb-3">Suivez-nous</h4>
              <SocialLinks />
            </div>
          </div>
        </div>
        <div className="border-t border-slate-800 py-5 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} {ASSO.name} — Association loi 1901
        </div>
      </footer>
      <IdeaBox />
    </div>
  );
}
